import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Card,
    Typography,
    message,
    Spin,
    Tag,
    Empty,
    List,
    Avatar
} from 'antd';
import {
    EyeOutlined,
    ClockCircleOutlined,
    UserOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import auctionService from '../../services/auctionService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import PageHeader from '../Common/PageHeader';
import env from '../../env';

const { Title, Text } = Typography;

const AcceptedInvitations = () => {
    const [loading, setLoading] = useState(true);
    const [invitations, setInvitations] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        loadInvitations();
    }, []);

    const loadInvitations = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getAcceptedInvitations();
            setInvitations(response.data);
        } catch (err) {
            message.error('Failed to load accepted invitations');
        } finally {
            setLoading(false);
        }
    };

    const handleViewAuction = (slug) => {
        navigate(`/auctions/${slug}`);
    };

    const columns = [
        {
            title: 'Auction',
            dataIndex: 'auction',
            key: 'auction',
            render: (auction) => (
                <Space>
                    <Avatar
                        shape="square"
                        size={50}
                        src={env.BACKEND_STORAGE_URL + '/' + auction.image}
                        style={{ 
                            width: 50, 
                            height: 50, 
                            borderRadius: 4, 
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f0f0f0'
                        }}
                    />
                    <Space direction="vertical" size="small">
                        <Text strong>{auction.title}</Text>
                        <Space>
                            <Tag color={auction.status === 'active' ? 'success' : 'default'}>
                                {auction.status.toUpperCase()}
                            </Tag>
                            <Tag color={auction.is_private ? 'orange' : 'green'}>
                                {auction.is_private ? 'PRIVATE' : 'PUBLIC'}
                            </Tag>
                        </Space>
                    </Space>
                </Space>
            )
        },
        {
            title: 'Start Time',
            dataIndex: 'auction',
            key: 'start_time',
            render: (auction) => (
                <Space>
                    <ClockCircleOutlined />
                    <Text>{moment(auction.start_time).format('MMM D, YYYY HH:mm')}</Text>
                </Space>
            )
        },
        {
            title: 'Accepted At',
            dataIndex: 'responded_at',
            key: 'responded_at',
            render: (date) => (
                <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text>{moment(date).format('MMM D, YYYY HH:mm')}</Text>
                </Space>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewAuction(record.auction.slug)}
                >
                    View Auction
                </Button>
            )
        }
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Accepted Invitations"
                description="View all private auctions you've accepted invitations to. These are the auctions you're currently participating in. Click 'View Auction' to access the auction details."
            />

            <Spin spinning={loading}>
                {invitations.length === 0 ? (
                    <Empty
                        description="You haven't accepted any auction invitations yet"
                        style={{ margin: '50px 0' }}
                    />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={invitations}
                        rowKey="id"
                        pagination={false}
                    />
                )}
            </Spin>
        </DashboardLayout>
    );
};

export default AcceptedInvitations; 